import { html, mount, safeUrl } from '../core/html.js';
import { get, session } from '../core/api.js';
import { propertyCard, emptyState, icon } from '../core/ui.js';
import { pct, rm, fmtDate, addDays, stateName } from '../core/format.js';
import { t } from '../core/i18n.js';
import { dateField, setDateField } from '../core/controls.js';
import { navigate } from '../app.js';

export function promoText(p) {
  const value = p.type === 'PERCENT' ? t('promo.percent_off', { v: pct(p.value) }) : t('promo.fixed_off', { v: rm(p.value) });
  const parts = [value];
  if (p.first_booking_only) parts.push(t('promo.first_booking_only'));
  if (p.ends_on) parts.push(t('promo.until', { date: fmtDate(p.ends_on) }));
  return parts.join(', ');
}

export async function render(main) {
  const cfg = session.config;
  const today = cfg.today;
  const cats = [['homestay', 'home'], ['villa', 'villa'], ['chalet', 'cabin'], ['hotel', 'building'], ['apartment', 'apartment'], ['farm_stay', 'leaf']];
  mount(main, html`
    <section class="hero wrap">
      <div class="hero-grid">
        <div>
          <span class="eyebrow">${t('home.eyebrow')}</span>
          <h1>${t('home.title')}</h1>
          <p class="lede">${t('home.lede')}</p>
          <div class="trust"><span>${icon('shield')}${t('home.trust_verified')}</span><span>${icon('check')}${t('home.trust_price')}</span><span>${icon('key')}${t('home.trust_secure')}</span></div>
        </div>
        <div class="hero-art" aria-hidden="true"><div class="card-art"><span class="halo"></span>
          <img src="/brand/mark-512.png" alt="" width="512" height="512">
          <span class="pill a">${icon('wave')}${t('home.art_nature')}</span>
          <span class="pill b">${icon('pin')}${t('home.art_local')}</span></div></div>
      </div>
      <form class="ticket" id="search" role="search" aria-label="${t('home.search_aria')}">
        <div class="seg"><label for="q">${t('home.where')}</label><input id="q" name="q" placeholder="${t('home.where_ph')}" autocomplete="off"></div>
        <div class="seg"><label for="ci">${t('booking.check_in')}</label>${dateField('check_in', { id: 'ci', min: today, placeholder: t('home.add_date') })}</div>
        <div class="seg"><label for="co">${t('booking.check_out')}</label>${dateField('check_out', { id: 'co', min: addDays(today, 1), placeholder: t('home.add_date') })}</div>
        <div class="seg"><label for="g">${t('booking.guests')}</label><select id="g" name="guests">${[1, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20].map((n) => html`<option value="${n}" ${n === 2 ? 'selected' : ''}>${t('common.people', { n })}</option>`)}</select></div>
        <div class="go"><button class="btn btn-primary" type="submit">${t('common.search')}</button></div>
      </form>
      <div class="cats" role="list">${cats.map(([k, ic]) => html`<a role="listitem" class="chip" href="#/search?property_type=${k}">${icon(ic)}${t(`ptype.${k}`)}</a>`)}<a role="listitem" class="chip" href="#/search?amenities=prayer_room">${icon('mosque')}${t('amenity.prayer_room')}</a><a role="listitem" class="chip" href="#/search?amenities=pool">${icon('pool')}${t('amenity.pool')}</a></div>
    </section>
    <section class="section wrap" id="featured"><div class="section-head"><h2>${t('home.featured')}</h2><a href="#/search?sort=recommended">${t('common.see_all')}</a></div><div class="cards four" id="featured-list"></div></section>
    <section class="section wrap" id="dest-sec"><div class="section-head"><h2>${t('home.destinations')}</h2></div><div class="dests" id="dests"></div></section>
    <section class="section wrap hidden" id="offers"></section>
    <section class="section wrap"><div class="section-head"><h2>${t('home.newest')}</h2><a href="#/search?sort=newest">${t('common.see_all')}</a></div><div class="cards four" id="newest"></div></section>
    <section class="section wrap hidden" id="hosts-sec"><div class="section-head"><h2>${t('home.top_hosts')}</h2></div><div class="dests" id="hosts"></div></section>
    <section class="section wrap"><div class="become">
      <div><span class="eyebrow">${t('home.become_eyebrow')}</span><h2>${t('home.become_title')}</h2><p>${t('home.become_text', { fee: pct(cfg.settings.platform_fee_bps) })}</p></div>
      <a class="btn" href="#/become-host">${t('home.become_cta')}</a></div></section>`);

  main.querySelector('#search').addEventListener('submit', (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    const qs = new URLSearchParams();
    for (const [k, v] of f.entries()) if (v) qs.set(k, v);
    if (qs.get('check_in') && qs.get('check_out') && qs.get('check_out') <= qs.get('check_in')) qs.delete('check_out');
    navigate('/search?' + qs.toString());
  });
  main.querySelector('#ci-v').addEventListener('change', (e) => {
    if (!e.target.value) return;
    const wrap = main.querySelector('#co').parentElement;
    wrap.dataset.min = addDays(e.target.value, 1);
    const co = main.querySelector('#co-v');
    if (!co.value || co.value <= e.target.value) setDateField(wrap, addDays(e.target.value, 1));
  });

  const wish = session.me ? new Set(await get('/api/wishlist/ids').catch(() => [])) : new Set();
  const [featured, newest, dests, promos, hosts] = await Promise.all([
    get('/api/properties?sort=recommended&page_size=8'),
    get('/api/properties?sort=newest&page_size=4'),
    get('/api/destinations'),
    get('/api/promotions/public').catch(() => []),
    get('/api/hosts/top').catch(() => []),
  ]);
  mount(main.querySelector('#featured-list'), featured.items.length ? featured.items.map((p) => propertyCard(p, { saved: wish.has(p.id) })) : emptyState(t('home.empty_title'), t('home.empty_text')));
  mount(main.querySelector('#newest'), newest.items.map((p) => propertyCard(p, { saved: wish.has(p.id) })));
  mount(main.querySelector('#dests'), dests.map((d) => html`<a class="dest" href="#/search?q=${encodeURIComponent(d.city)}">${d.photo ? html`<img src="${safeUrl(d.photo)}" alt="" loading="lazy">` : html`<span class="ph"></span>`}<span><b>${d.city}</b><span class="muted small">${t('home.dest_count', { state: stateName(d.state), n: d.count })}</span></span></a>`));
  if (promos.length) {
    const o = main.querySelector('#offers');
    o.classList.remove('hidden');
    mount(o, html`<div class="section-head"><h2>${t('home.offers')}</h2></div><div class="grid">${promos.slice(0, 3).map((p) => html`<div class="offer"><div><b>${t('promo.use_code')}</b><div class="small muted">${promoText(p)}</div></div><code>${p.code}</code></div>`)}</div>`);
  }
  if (hosts.length) {
    main.querySelector('#hosts-sec').classList.remove('hidden');
    mount(main.querySelector('#hosts'), hosts.map((h) => html`<div class="dest"><span class="avatar">${h.display_name.slice(0, 1)}</span><span><b data-ugc>${h.display_name}</b><span class="muted small">${t('home.host_rating', { rating: h.rating_avg, n: h.rating_count })}</span></span></div>`));
  }
}
